import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileRow } from '@/components/ged/FileRow'
import { FileDrawer } from '@/components/ged/FileDrawer'
import { Button } from '@/components/ui/button'
import { listDocuments, documentKeys } from '@/lib/api/documents'
import { useSelectedDocParam } from '@/lib/useSelectedDocParam'
import type { ListDocumentsParams } from '@/lib/types/ged'

/** Quantos arquivos a mais cada clique em "Mostrar mais" traz. */
const PAGE_STEP = 20

type Props = {
  areaId: string
}

/**
 * Arquivos anexados diretamente a um nó que renderiza grade de cards (nível 1 ou 2 com
 * subáreas). Mostrada abaixo dos cards — ex. o regulamento geral que pertence ao índice e
 * não a nenhuma das subáreas (spec 2026-09-13).
 *
 * Deliberadamente enxuta: sem filtro por tipo, sem busca e sem paginação numerada — isso é
 * papel da AreaContentPage, onde os arquivos são o conteúdo principal da página. Aqui eles
 * são um complemento dos cards, e uma área que acumula muitos arquivos soltos deveria
 * organizá-los em subáreas. O "Mostrar mais" existe só para o caso extremo não quebrar.
 */
export function AreaDirectFiles({ areaId }: Props) {
  const [pageSize, setPageSize] = useState(PAGE_STEP)
  const { selectedDocId, setSelectedDocId } = useSelectedDocParam()

  const params: ListDocumentsParams = { areaId, page: 1, pageSize }
  const { data, isError } = useQuery({
    queryKey: documentKeys.list(params),
    queryFn: () => listDocuments(params),
    // Mantém a lista anterior na tela enquanto a leva maior carrega, em vez de piscar.
    placeholderData: (prev) => prev,
  })

  const files = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  // Sem arquivos próprios e sem erro (ou ainda carregando): a página de cards fica como
  // sempre foi — numa página de índice, "nenhum arquivo" é o estado normal, e um estado
  // vazio só somaria ruído. Um erro, porém, não é um estado vazio: precisa aparecer,
  // inclusive pra quem acabou de subir um arquivo e vê a tela não refletir isso.
  const showSection = isError || totalCount > 0

  return (
    <>
      {showSection && (
        <div style={{ marginTop: 28 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, color: 'var(--ev-fg-strong)', margin: 0 }}>Arquivos desta área</h3>
            {!isError && <span style={{ fontSize: 'var(--fs-small)', color: 'var(--ev-fg-muted)' }}>{totalCount}</span>}
          </div>

          <div style={{ background: 'var(--ev-bg-elevated)', border: '1px solid var(--ev-border)', borderRadius: 10, overflow: 'hidden' }}>
            {isError ? (
              <div style={{ padding: '10px 16px', color: 'var(--ev-fg-muted)', fontSize: 'var(--fs-small)' }}>
                Não foi possível carregar os arquivos desta área.
              </div>
            ) : (
              files.map((doc) => (
                <FileRow key={doc.id} doc={doc} onClick={setSelectedDocId} density="compact" isSelected={doc.id === selectedDocId} />
              ))
            )}
          </div>

          {!isError && files.length < totalCount && (
            <div style={{ marginTop: 12 }}>
              <Button variant="outline" size="sm" onClick={() => setPageSize((s) => s + PAGE_STEP)}>
                Mostrar mais
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Renderizado incondicionalmente: um deep-link ?doc=<id> precisa abrir a gaveta
          mesmo quando a seção de arquivos está oculta (área sem arquivos próprios) — o
          FileDrawer busca o documento por id sozinho, sem depender desta listagem. */}
      <FileDrawer documentId={selectedDocId} onClose={() => setSelectedDocId(null)} />
    </>
  )
}
